"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { FilterMultiLayoutType } from "@/features/filters/components/filter-multi/filter-multi-form";
import { cn } from "@/lib/utils";
import type { FilterMultiType } from "../../types/filter-multi.type";
import { FilterMultiItem } from "./filter-multi-item";
import { FilterMultiItemButton } from "./filter-multi-item-button";

type FilterMultiProps = {
  filter: FilterMultiType;
  paramName: string;
  collapsible?: boolean | number | string;
  listWrapperClass?: string;
  type?: "checkbox" | "button";
  multiple?: boolean;
  stretch?: boolean;
  layout?: FilterMultiLayoutType;
  collapsibleButtonLabel?: string;
};

/**
 * Renders a multi-select filter component.
 *
 * @param {FilterMultiProps} props - The props for the component.
 * @param {FilterMultiType} props.filter - The filter object.
 * @param {string} props.paramName - The name of the URL query parameter.
 * @param {boolean|number} [props.collapsible=false] - Whether the filter should be collapsible and how many options should be shown before collapsing.
 * @return {JSX.Element} The rendered filter component.
 */
export const FilterMultiAutoupdate = ({
  filter,
  paramName,
  collapsible = false,
  type = "checkbox",
  listWrapperClass,
  multiple = true,
  stretch = false,
  layout = "default",
  collapsibleButtonLabel,
}: FilterMultiProps) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const t = useTranslations("filters");

  /**
   * Updates the selected values state when the URL query parameters change.
   */
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const tags = params.get(paramName)?.split(",") || [];

    setSelectedValues(tags);
  }, []);

  useEffect(() => {
    setSelectedValues(searchParams.get(paramName)?.split(",") || []);
  }, [searchParams]);

  /**
   * Debounced function to update the URL query parameters with the selected
   * values.
   *
   * @param {string[]} values - The selected values for the filter.
   * @return {void} This function does not return a value.
   */
  const debouncedUpdateData = useDebouncedCallback((values: string[]) => {
    const params = new URLSearchParams(searchParams);

    if (values.length > 0) {
      params.set(paramName, values.join(","));
    } else {
      params.delete(paramName);
    }

    params.delete("page");

    replace(`${pathname}?${params.toString()}`);
  }, 500);

  /**
   * Handles the change event for a checkbox. Updates the selected values state
   * and debounces the update of the URL query parameters.
   *
   * @param {string} value - The value of the checkbox that was changed.
   * @return {void} This function does not return a value.
   */
  const handleCheckboxChange = (value: string, checked: boolean | string) => {
    setSelectedValues((prevState) => {
      let newState = prevState;
      if (multiple) {
        newState = !checked
          ? prevState.filter((item) => item !== value)
          : [...prevState, value];
      } else {
        newState = !checked ? [] : [value];
      }
      debouncedUpdateData(newState);
      return newState;
    });
  };

  const Item = type === "checkbox" ? FilterMultiItem : FilterMultiItemButton;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          listWrapperClass,
          type === "button" && "flex flex-wrap gap-2",
          layout === "columns" && "grid grid-cols-2 gap-2",
          layout === "horizontalScroll" &&
            "flex flex-row flex-nowrap overflow-x-scroll",
        )}
      >
        {filter.options.map((option, index) =>
          (collapsible &&
            typeof collapsible === "number" &&
            collapsible < index) ||
          (typeof collapsible === "string" && option[collapsible]) ? (
            <CollapsibleContent key={option.value}>
              <Item
                handleChange={handleCheckboxChange}
                selectedValues={selectedValues}
                option={option}
                stretch={stretch}
              />
            </CollapsibleContent>
          ) : (
            <Item
              key={option.value}
              handleChange={handleCheckboxChange}
              selectedValues={selectedValues}
              option={option}
              stretch={stretch}
            />
          ),
        )}
      </div>
      {collapsible && (
        <CollapsibleTrigger asChild>
          <Button
            variant="link"
            className="mt-4 min-h-0 p-0 hover:no-underline"
          >
            {isOpen
              ? t("collapse")
              : collapsibleButtonLabel
                ? collapsibleButtonLabel
                : t("show_all")}
          </Button>
        </CollapsibleTrigger>
      )}
    </Collapsible>
  );
};
